import React from 'react';
import { Link } from 'react-router-dom';
import { Warning, Lightning } from '@phosphor-icons/react';
import { useCompany } from '../context/CompanyContext';

// Banner that surfaces approaching/over-limit plan usage. Sits under the
// TrialBanner in Layout so owners see it every time they navigate.
//
// Thresholds:
//   pct >= 100 → 'exceeded'  (rose, hard nudge)
//   pct >= 80  → 'warning'   (amber, soft nudge)
// Uses the worst-offending metric (whichever has the highest pct).

const METRICS = [
  { key: 'users',            label: 'users' },
  { key: 'skus',             label: 'SKUs' },
  { key: 'orders_last_30d',  label: 'orders / 30d', limitKey: 'orders_per_month' },
];

export default function UsageAlertBanner() {
  const { company } = useCompany();
  if (!company) return null;
  if (company.status === 'pending_payment') return null;   // pending_payment banner already covers this

  const usage = company.usage || {};
  const limits = company.limits || {};

  let worst = null;
  for (const m of METRICS) {
    const used = usage[m.key] || 0;
    const lim = limits[m.limitKey || m.key];
    if (!lim || lim === 0) continue;
    const pct = Math.round((used / lim) * 100);
    if (pct < 80) continue;
    if (!worst || pct > worst.pct) {
      worst = { ...m, used, lim, pct };
    }
  }
  if (!worst) return null;

  const exceeded = worst.pct >= 100;
  return (
    <div
      data-testid="usage-alert-banner"
      className={`w-full text-xs font-bold uppercase tracking-widest px-4 py-2 flex items-center justify-center gap-3 ${
        exceeded ? 'bg-rose-600 text-white' : 'bg-amber-500 text-foreground'
      }`}
    >
      {exceeded ? <Warning size={14} weight="fill" /> : <Lightning size={14} weight="fill" />}
      <span data-testid="usage-alert-text">
        {exceeded ? 'Limit reached · ' : 'Approaching limit · '}
        {worst.used}/{worst.lim} {worst.label} used on your {company.plan_name} plan
      </span>
      <Link
        to="/billing"
        data-testid="usage-alert-upgrade"
        className="px-3 py-1 bg-card text-foreground hover:bg-muted border border-slate-900 transition"
      >
        Upgrade
      </Link>
    </div>
  );
}
